import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useStore } from "../../helpers";
import { safe_get } from "../../report-visuals";
import { ElectionResults } from "../../flexistore/shared/election_results";
import { FSD_S3_BUCKET } from "../../flexistore/renderkeys/ac-bank/acbank_dao";
import BoothWiseResults from "./hook/c3-boothwise-results";

const C3Historical = observer(() => {
    const { t } = useTranslation("constituency_command_center");
    const rootStore = useStore();
    const { c3Store, projectStore } = useStore();

    const [loading, setLoading] = useState(false);

    const [data, setData] = useState<any>(null);
    const [boothData, setBoothData] = useState<any>(null);

    const [activeElection, setActiveElection] = useState<string>("");

    const [activeTab, setActiveTab] = useState<"election" | "booth">(
        "election"
    );

    const electionYear = activeElection?.split(" ")[0];

    const projectProperties = projectStore.getProjectProperty(
        "project_state",
        {}
    );

    const databank = safe_get(projectProperties, "databank", null);
    const acno = safe_get(projectProperties, "acno", null);

    const fetchHistoricalData = async () => {
        if (!acno || !databank) return;

        setLoading(true);

        try {
            const payload = { acno, databank };

            const response = await c3Store.get_form_20_data(payload);

            /**
             * Election Results Path
             */
            const electionFilePath = response?.[0]?.r1?.election_results;

            if (electionFilePath) {
                const signedUrl = await rootStore.get_s3_url(
                    FSD_S3_BUCKET,
                    electionFilePath
                );

                const electionJson =
                    await rootStore.get_json_resource(signedUrl);

                setData(electionJson);

                if (
                    electionJson &&
                    typeof electionJson === "object" &&
                    !electionJson.candidates
                ) {
                    const keys = Object.keys(electionJson);

                    if (keys.length > 0) {
                        setActiveElection(keys[0]);
                    }
                }
            }

            /**
             * Booth Results Path
             */

            const boothFilePath = response?.[0]?.r1?.booth_results;

            if (boothFilePath) {
                const boothSignedUrl = await rootStore.get_s3_url(
                    FSD_S3_BUCKET,
                    boothFilePath
                );

                const boothJson =
                    await rootStore.get_json_resource(boothSignedUrl);

                setBoothData(boothJson);
                console.log("Booth JSON:", boothJson);
            }
        } catch (err) {
            console.error("Error loading historical data:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHistoricalData();
    }, [acno, databank]);

    if (loading) return <div className="p-4">Loading...</div>;

    if (!data) return null;

    const isMultiElection = typeof data === "object" && !data.candidates;

    return (
        <div className="pl-4">
            {/* Title */}
            <div className="pb-4 text-2xl font-bold uppercase">
                {t("historical")}
            </div>

            {/* Election Year Tabs */}

            {isMultiElection && (
                <div className="mb-6 flex flex-wrap">
                    {Object.keys(data)
                        .sort((a, b) => {
                            const yearA = parseInt(a.split(" ")[0]);
                            const yearB = parseInt(b.split(" ")[0]);
                            return yearA - yearB;
                        })
                        .map((electionKey) => {
                            const activeStyle =
                                activeElection === electionKey
                                    ? "bg-indigo-500 text-white font-semibold shadow"
                                    : "bg-gray-200 border border-gray-300";

                            return (
                                <div
                                    key={electionKey}
                                    className={`mt-2 px-4 py-2 text-sm ${activeStyle} mr-3 cursor-pointer rounded-full transition`}
                                    onClick={() =>
                                        setActiveElection(electionKey)
                                    }
                                >
                                    {electionKey}
                                </div>
                            );
                        })}
                </div>
            )}

            {/* Main Tabs */}

            <div className="mb-6 flex border-b">
                <div
                    onClick={() => setActiveTab("election")}
                    className={`cursor-pointer px-5 py-2 font-medium ${
                        activeTab === "election"
                            ? "border-b-2 border-indigo-600 text-indigo-600"
                            : "text-gray-500"
                    }`}
                >
                    Election Results
                </div>

                <div
                    onClick={() => setActiveTab("booth")}
                    className={`cursor-pointer px-5 py-2 font-medium ${
                        activeTab === "booth"
                            ? "border-b-2 border-indigo-600 text-indigo-600"
                            : "text-gray-500"
                    }`}
                >
                    Booth Wise Results
                </div>
            </div>

            {/* Election Results Tab */}

            {activeTab === "election" && (
                <>
                    {isMultiElection && activeElection && (
                        <ElectionResults
                            name={activeElection}
                            ds={data[activeElection]}
                        />
                    )}

                    {!isMultiElection && (
                        <ElectionResults name="Historical" ds={data} />
                    )}
                </>
            )}

            {/* Booth Wise Results Tab */}

            {activeTab === "booth" && (
                <>
                    {boothData && electionYear && boothData[electionYear] && (
                        <BoothWiseResults data={boothData[electionYear]} />
                    )}

                    {!boothData && (
                        <div className="p-4 text-sm text-gray-500">
                            Booth data not available
                        </div>
                    )}
                </>
            )}
        </div>
    );
});

export default C3Historical;
